import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { analytics } from '../utils/analytics';
import { Annotation, LearnApi, LearnState, QuizResult, SignInRequired, TutorMessage } from './api';
import { describeRange, findRange, highlightRange, removeMarks, QuoteSelector } from './anchor';
import { setNavRead, setQuizPassed } from './reading';
import { session } from './session';
import { scroller } from './reading';
import PatternBoard from '../pattern-board-app/PatternBoard';

// One React root for the interactive layer, rendering through portals into the mount points
// Article.cshtml leaves, and working on the article DOM directly for marks.

interface Props {
  article: HTMLElement;
  title: string;
  mounts: {
    toolbar: HTMLElement;
    tutor: HTMLElement;
    quiz: HTMLElement | null;
    comments: HTMLElement | null;
  };
}

interface QuizQuestion {
  q: string;
  fen?: string | null;
  options: string[];
}

const KIND_CLASS = ['lb-hl', 'lb-hl lb-hl--note', 'lb-hl lb-hl--comment'];

function handleError(e: unknown, fallback: string): void {
  if (e instanceof SignInRequired) {
    session.requireSignIn();
    return;
  }
  const message = e instanceof Error ? e.message : fallback;
  window.alert(message);
}

export default function LearnApp({ article, title, mounts }: Props): React.ReactElement {
  const slug = session.slug;
  const [state, setState] = useState<LearnState | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [comments, setComments] = useState<Annotation[]>([]);

  // ---- Load state, apply existing marks ---------------------------------------------------
  useEffect(() => {
    LearnApi.state(slug)
      .then((s) => {
        setState(s);
        setAnnotations(s.annotations);
        setComments(s.comments);
      })
      .catch(() => setState({ signedIn: false, webmaster: false, read: [], quiz: {}, annotations: [], comments: [], tutor: [] }));
  }, [slug]);

  const applied = useRef(new Set<number>());
  const applyMark = useCallback(
    (a: Annotation, mine: boolean) => {
      if (!a.quote || applied.current.has(a.id)) return;
      const range = findRange(article, { quote: a.quote, prefix: a.prefix || '', suffix: a.suffix || '' });
      if (!range) return;
      const cls = mine ? KIND_CLASS[a.kind] : 'lb-hl lb-hl--other';
      highlightRange(range, cls, { ann: String(a.id) });
      applied.current.add(a.id);
    },
    [article]
  );

  useEffect(() => {
    annotations.forEach((a) => applyMark(a, true));
    comments.filter((c) => !c.mine && c.quote).forEach((c) => applyMark(c, false));
  }, [annotations, comments, applyMark]);

  // ---- Mark as read -----------------------------------------------------------------------
  const [isRead, setIsRead] = useState<boolean>(() =>
    document.querySelector('[data-learn-mark]')?.classList.contains('is-read') ?? false
  );
  useEffect(() => {
    const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-learn-mark]'));
    const paint = (read: boolean) => {
      buttons.forEach((b) => {
        b.classList.toggle('is-read', read);
        b.setAttribute('aria-pressed', String(read));
        const label = b.querySelector('[data-learn-mark-label]');
        if (label) label.textContent = read ? 'Read' : 'Mark as read';
      });
      setNavRead(slug, read);
    };
    const onClick = async () => {
      if (!session.requireSignIn()) return;
      const next = !isRead;
      try {
        await LearnApi.progress(slug, next);
        setIsRead(next);
        paint(next);
        analytics.track('learn_mark_read', { slug, read: next });
      } catch (e) {
        handleError(e, 'Could not save your progress.');
      }
    };
    buttons.forEach((b) => b.addEventListener('click', onClick));
    return () => buttons.forEach((b) => b.removeEventListener('click', onClick));
  }, [slug, isRead]);

  // ---- Selection toolbar ------------------------------------------------------------------
  const [selection, setSelection] = useState<{ sel: QuoteSelector; x: number; y: number; range: Range } | null>(null);
  const [popover, setPopover] = useState<{ ann: Annotation; x: number; y: number } | null>(null);
  const [compose, setCompose] = useState<{ kind: 1 | 2 } | null>(null);
  const [composeText, setComposeText] = useState('');

  useEffect(() => {
    const onUp = () => {
      window.setTimeout(() => {
        const s = window.getSelection();
        if (!s || s.isCollapsed || s.rangeCount === 0) {
          if (!compose) setSelection(null);
          return;
        }
        const range = s.getRangeAt(0);
        if (!article.contains(range.commonAncestorContainer)) {
          if (!compose) setSelection(null);
          return;
        }
        const sel = describeRange(article, range);
        if (!sel || sel.quote.trim().length < 2) return;
        const rect = range.getBoundingClientRect();
        setSelection({ sel, x: rect.left + rect.width / 2, y: rect.top, range: range.cloneRange() });
        setCompose(null);
        setComposeText('');
      }, 10);
    };
    const onDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('.lb-toolbar')) return;
      if (compose) return;
      setSelection(null);
    };
    document.addEventListener('mouseup', onUp);
    document.addEventListener('keyup', onUp);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('keyup', onUp);
      document.removeEventListener('mousedown', onDown);
    };
  }, [article, compose]);

  useEffect(() => {
    const sc = scroller();
    const onScroll = () => {
      if (!compose) setSelection(null);
      setPopover(null);
    };
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => sc.removeEventListener('scroll', onScroll);
  }, [compose]);

  const kindName = (k: number) => (['highlight', 'note', 'comment'] as const)[k];

  const saveAnnotation = async (kind: 0 | 1 | 2, body?: string) => {
    if (!selection) return;
    if (!session.requireSignIn()) return;
    try {
      const created = await LearnApi.createAnnotation({ slug, kind, ...selection.sel, body: body || null });
      setAnnotations((list) => [...list, created]);
      if (kind === 2) setComments((list) => [...list, created]);
      analytics.track('learn_annotate', { slug, kind: kindName(kind) });
      window.getSelection()?.removeAllRanges();
      setSelection(null);
      setCompose(null);
      setComposeText('');
    } catch (e) {
      handleError(e, 'Could not save that.');
    }
  };

  // ---- Popover on an existing mark --------------------------------------------------------
  const [editText, setEditText] = useState('');
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const mark = (e.target as HTMLElement).closest<HTMLElement>('mark[data-ann]');
      if (!mark || !article.contains(mark)) {
        if (!(e.target as HTMLElement).closest('.lb-popover')) setPopover(null);
        return;
      }
      const id = Number(mark.dataset.ann);
      const ann = annotations.find((a) => a.id === id) || comments.find((c) => c.id === id);
      if (!ann) return;
      const rect = mark.getBoundingClientRect();
      setPopover({ ann, x: Math.min(rect.left, window.innerWidth - 380), y: rect.bottom + 6 });
      setEditText(ann.body || '');
      e.preventDefault();
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [article, annotations, comments]);

  const deleteAnnotation = async (a: Annotation) => {
    try {
      await LearnApi.deleteAnnotation(a.id);
      removeMarks(a.id);
      applied.current.delete(a.id);
      setAnnotations((list) => list.filter((x) => x.id !== a.id));
      setComments((list) => list.filter((x) => x.id !== a.id));
      setPopover(null);
    } catch (e) {
      handleError(e, 'Could not delete that.');
    }
  };

  const updateAnnotation = async (a: Annotation, body: string) => {
    try {
      await LearnApi.updateAnnotation(a.id, body);
      const patch = (list: Annotation[]) => list.map((x) => (x.id === a.id ? { ...x, body } : x));
      setAnnotations(patch);
      setComments(patch);
      setPopover(null);
    } catch (e) {
      handleError(e, 'Could not save that.');
    }
  };

  // ---- Tutor ------------------------------------------------------------------------------
  const [tutorOpen, setTutorOpen] = useState(false);
  const [tutorQuote, setTutorQuote] = useState<string | null>(null);
  const [thread, setThread] = useState<TutorMessage[]>([]);
  const [tutorInput, setTutorInput] = useState('');
  const [tutorBusy, setTutorBusy] = useState(false);
  const threadEnd = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (state) setThread(state.tutor);
  }, [state]);
  useEffect(() => {
    document.querySelectorAll('[data-learn-tutor-open]').forEach((b) =>
      b.addEventListener('click', () => {
        setTutorQuote(null);
        setTutorOpen(true);
      })
    );
  }, []);
  useEffect(() => {
    if (tutorOpen) threadEnd.current?.scrollIntoView({ block: 'end' });
  }, [tutorOpen, thread, tutorBusy]);

  const ask = async (text: string) => {
    const message = text.trim();
    if (!message || tutorBusy) return;
    if (!session.requireSignIn()) return;
    const quote = tutorQuote;
    setTutorBusy(true);
    setTutorInput('');
    setThread((t) => [
      ...t,
      { id: -Date.now(), role: 'user', content: quote ? `> ${quote}\n\n${message}` : message, createdAt: new Date().toISOString() },
    ]);
    try {
      const r = await LearnApi.tutor(slug, message, quote);
      setThread((t) => [...t, { id: r.id, role: 'assistant', content: r.reply, createdAt: new Date().toISOString() }]);
      analytics.track('learn_tutor_ask', { slug, with_quote: !!quote });
      setTutorQuote(null);
    } catch (e) {
      setThread((t) => t.slice(0, -1));
      handleError(e, 'The tutor could not answer.');
    } finally {
      setTutorBusy(false);
    }
  };

  const clearThread = async () => {
    try {
      await LearnApi.clearTutor(slug);
      setThread([]);
    } catch (e) {
      handleError(e, 'Could not clear the conversation.');
    }
  };

  // ---- Quiz -------------------------------------------------------------------------------
  const quizQuestions = useMemo<QuizQuestion[]>(() => {
    const el = document.getElementById('learn-quiz-data');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent || '[]');
    } catch {
      return [];
    }
  }, []);
  const [answers, setAnswers] = useState<number[]>(() => quizQuestions.map(() => -1));
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [quizBusy, setQuizBusy] = useState(false);

  const submitQuiz = async () => {
    if (answers.some((a) => a < 0)) {
      window.alert('Answer every question first.');
      return;
    }
    setQuizBusy(true);
    try {
      const r = await LearnApi.quiz(slug, answers);
      setQuizResult(r);
      analytics.track('learn_quiz_submit', { slug, score: r.score, total: r.total });
      if (r.saved && r.score === r.total) setQuizPassed(slug);
    } catch (e) {
      handleError(e, 'Could not grade the quiz.');
    } finally {
      setQuizBusy(false);
    }
  };

  // ---- Comments ---------------------------------------------------------------------------
  const [commentText, setCommentText] = useState('');
  const postComment = async () => {
    if (!session.requireSignIn()) return;
    const body = commentText.trim();
    if (!body) return;
    try {
      const created = await LearnApi.createAnnotation({ slug, kind: 2, body });
      setComments((list) => [...list, created]);
      setCommentText('');
      analytics.track('learn_annotate', { slug, kind: 'comment' });
    } catch (e) {
      handleError(e, 'Could not post the comment.');
    }
  };

  const signedIn = state?.signedIn ?? session.signedIn;
  const webmaster = state?.webmaster ?? false;

  // ---- Render -----------------------------------------------------------------------------
  return (
    <>
      {createPortal(
        selection ? (
          <div
            className="lb-toolbar"
            style={{ left: selection.x, top: selection.y }}
            role="toolbar"
            aria-label="Selection actions"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {!compose ? (
              <>
                <button type="button" onClick={() => saveAnnotation(0)}>
                  Highlight
                </button>
                <button type="button" onClick={() => setCompose({ kind: 1 })}>
                  Note
                </button>
                <button type="button" onClick={() => setCompose({ kind: 2 })}>
                  Comment
                </button>
                <button
                  type="button"
                  className="lb-toolbar-tutor"
                  onClick={() => {
                    setTutorQuote(selection.sel.quote);
                    setTutorOpen(true);
                    setSelection(null);
                    window.getSelection()?.removeAllRanges();
                  }}
                >
                  Ask the tutor
                </button>
              </>
            ) : (
              <div className="lb-compose">
                <div className="lb-compose-quote">“{selection.sel.quote.length > 90 ? selection.sel.quote.slice(0, 90) + '…' : selection.sel.quote}”</div>
                <textarea
                  autoFocus
                  rows={3}
                  placeholder={compose.kind === 1 ? 'Private note, only you see it' : 'Public comment, other readers see it'}
                  value={composeText}
                  onChange={(e) => setComposeText(e.target.value)}
                />
                <div className="lb-compose-actions">
                  <button type="button" className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => saveAnnotation(compose.kind, composeText)} disabled={!composeText.trim()}>
                    {compose.kind === 1 ? 'Save note' : 'Post comment'}
                  </button>
                  <button type="button" className="lb-btn lb-btn--sm" onClick={() => { setCompose(null); setSelection(null); }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null,
        mounts.toolbar
      )}

      {popover &&
        createPortal(
          <div className="lb-popover" style={{ left: popover.x, top: popover.y }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="lb-popover-head">
              <span>{popover.ann.kind === 0 ? 'Highlight' : popover.ann.kind === 1 ? 'Your note' : popover.ann.mine ? 'Your comment' : `Comment by ${popover.ann.author}`}</span>
              <button type="button" className="lb-popover-close" onClick={() => setPopover(null)} aria-label="Close">
                &times;
              </button>
            </div>
            {popover.ann.mine && popover.ann.kind !== 0 ? (
              <>
                <textarea rows={3} value={editText} onChange={(e) => setEditText(e.target.value)} />
                <div className="lb-compose-actions">
                  <button type="button" className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => updateAnnotation(popover.ann, editText)} disabled={!editText.trim()}>
                    Save
                  </button>
                  <button type="button" className="lb-btn lb-btn--sm lb-btn--danger" onClick={() => deleteAnnotation(popover.ann)}>
                    Delete
                  </button>
                </div>
              </>
            ) : popover.ann.mine ? (
              <div className="lb-compose-actions">
                <button
                  type="button"
                  className="lb-btn lb-btn--sm"
                  onClick={() => {
                    setTutorQuote(popover.ann.quote);
                    setTutorOpen(true);
                    setPopover(null);
                  }}
                >
                  Ask the tutor about this
                </button>
                <button type="button" className="lb-btn lb-btn--sm lb-btn--danger" onClick={() => deleteAnnotation(popover.ann)}>
                  Remove highlight
                </button>
              </div>
            ) : (
              <>
                <p className="lb-popover-body">{popover.ann.body}</p>
                {webmaster && (
                  <button type="button" className="lb-btn lb-btn--sm lb-btn--danger" onClick={() => deleteAnnotation(popover.ann)}>
                    Delete (webmaster)
                  </button>
                )}
              </>
            )}
          </div>,
          document.body
        )}

      {createPortal(
        <div className={`lb-tutor ${tutorOpen ? 'is-open' : ''}`} aria-hidden={!tutorOpen}>
          <div className="lb-tutor-head">
            <div>
              <div className="lb-tutor-title">Tutor</div>
              <div className="lb-tutor-sub">{title}</div>
            </div>
            <div className="lb-tutor-head-actions">
              {thread.length > 0 && (
                <button type="button" className="lb-tutor-iconbtn" onClick={clearThread} title="Clear conversation">
                  Clear
                </button>
              )}
              <button type="button" className="lb-tutor-iconbtn" onClick={() => setTutorOpen(false)} aria-label="Close tutor">
                &times;
              </button>
            </div>
          </div>
          <div className="lb-tutor-thread">
            {thread.length === 0 && (
              <div className="lb-tutor-empty">
                <p>Ask anything about this page. The tutor answers from the article{signedIn ? ' and from the mistakes in your own repair queue' : ''}.</p>
                {!signedIn && (
                  <p>
                    <a href={session.loginUrl}>Sign in</a> to ask. It takes an email and nothing else.
                  </p>
                )}
              </div>
            )}
            {thread.map((m) => (
              <div key={m.id} className={`lb-msg lb-msg--${m.role}`}>
                <div className="lb-msg-role">{m.role === 'user' ? 'You' : 'Tutor'}</div>
                <div className="lb-msg-body">{renderMessage(m.content)}</div>
              </div>
            ))}
            {tutorBusy && (
              <div className="lb-msg lb-msg--assistant">
                <div className="lb-msg-role">Tutor</div>
                <div className="lb-msg-body lb-msg-thinking">Thinking…</div>
              </div>
            )}
            <div ref={threadEnd} />
          </div>
          <div className="lb-tutor-suggest">
            {['Explain this more simply', 'Give me another example', 'How does this apply to my games?'].map((s) => (
              <button key={s} type="button" onClick={() => ask(s)} disabled={tutorBusy}>
                {s}
              </button>
            ))}
          </div>
          {tutorQuote && (
            <div className="lb-tutor-quote">
              <span>“{tutorQuote.length > 120 ? tutorQuote.slice(0, 120) + '…' : tutorQuote}”</span>
              <button type="button" onClick={() => setTutorQuote(null)} aria-label="Remove quote">
                &times;
              </button>
            </div>
          )}
          <form
            className="lb-tutor-form"
            onSubmit={(e) => {
              e.preventDefault();
              ask(tutorInput);
            }}
          >
            <textarea
              rows={2}
              value={tutorInput}
              placeholder="Ask your tutor…"
              onChange={(e) => setTutorInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  ask(tutorInput);
                }
              }}
            />
            <button type="submit" className="lb-btn lb-btn--primary" disabled={tutorBusy || !tutorInput.trim()} aria-label="Send">
              Send
            </button>
          </form>
        </div>,
        mounts.tutor
      )}

      {mounts.quiz &&
        quizQuestions.length > 0 &&
        createPortal(
          <div className="lb-quiz-body">
            {quizQuestions.map((q, i) => {
              const r = quizResult?.results[i];
              return (
                <div key={i} className={`lb-q ${r ? (r.correct ? 'is-correct' : 'is-wrong') : ''}`}>
                  <div className="lb-q-text">
                    <span className="lb-q-num">{i + 1}</span> {q.q}
                  </div>
                  {q.fen && (
                    <div className="lb-q-board">
                      <PatternBoard line={[]} startFen={q.fen} mode="view" hint={null} />
                    </div>
                  )}
                  <div className="lb-q-options">
                    {q.options.map((opt, j) => (
                      <label key={j} className={`lb-q-option ${answers[i] === j ? 'is-chosen' : ''} ${r && j === r.answer ? 'is-answer' : ''}`}>
                        <input
                          type="radio"
                          name={`q${i}`}
                          checked={answers[i] === j}
                          disabled={!!quizResult}
                          onChange={() => setAnswers((a) => a.map((v, k) => (k === i ? j : v)))}
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                  {r && <div className="lb-q-explain">{r.correct ? 'Correct. ' : 'Not quite. '}{r.explanation}</div>}
                </div>
              );
            })}
            <div className="lb-quiz-actions">
              {!quizResult ? (
                <button type="button" className="lb-btn lb-btn--primary" onClick={submitQuiz} disabled={quizBusy}>
                  Check answers
                </button>
              ) : (
                <>
                  <div className="lb-quiz-score">
                    {quizResult.score}/{quizResult.total}
                    {quizResult.score === quizResult.total ? ' · Perfect.' : ''}
                    {!quizResult.saved ? (
                      <>
                        {' '}
                        <a href={session.loginUrl}>Sign in</a> to save your score.
                      </>
                    ) : ' Saved.'}
                  </div>
                  <button
                    type="button"
                    className="lb-btn"
                    onClick={() => {
                      setQuizResult(null);
                      setAnswers(quizQuestions.map(() => -1));
                    }}
                  >
                    Try again
                  </button>
                </>
              )}
            </div>
          </div>,
          mounts.quiz
        )}

      {mounts.comments &&
        createPortal(
          <div className="lb-comments-body">
            {comments.length === 0 && <p className="lb-muted">No comments yet. Select a sentence to comment on it, or write below.</p>}
            {comments.map((c) => (
              <div key={c.id} className="lb-comment">
                <div className="lb-comment-head">
                  <strong>{c.mine ? 'You' : c.author}</strong>
                  <span>{new Date(c.createdAt).toLocaleDateString()}</span>
                  {(c.mine || webmaster) && (
                    <button type="button" className="lb-comment-delete" onClick={() => deleteAnnotation(c)}>
                      Delete
                    </button>
                  )}
                </div>
                {c.quote && <blockquote>{c.quote.length > 200 ? c.quote.slice(0, 200) + '…' : c.quote}</blockquote>}
                <p>{c.body}</p>
              </div>
            ))}
            {signedIn ? (
              <form
                className="lb-comment-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  postComment();
                }}
              >
                <textarea rows={3} value={commentText} placeholder="Add a comment for other readers" onChange={(e) => setCommentText(e.target.value)} />
                <button type="submit" className="lb-btn lb-btn--primary" disabled={!commentText.trim()}>
                  Post comment
                </button>
              </form>
            ) : (
              <p className="lb-muted">
                <a href={session.loginUrl}>Sign in</a> to comment.
              </p>
            )}
          </div>,
          mounts.comments
        )}
    </>
  );
}

// The tutor's light Markdown: paragraphs, bullets, bold and inline code only, so a reply can't inject
// markup.
function renderMessage(text: string): React.ReactNode {
  const blocks = text.split(/\n{2,}/);
  return blocks.map((block, i) => {
    const lines = block.split('\n');
    if (lines.every((l) => /^\s*([-*•]|\d+[.)])\s+/.test(l))) {
      return (
        <ul key={i}>
          {lines.map((l, j) => (
            <li key={j}>{inline(l.replace(/^\s*([-*•]|\d+[.)])\s+/, ''))}</li>
          ))}
        </ul>
      );
    }
    if (lines.every((l) => l.startsWith('>'))) {
      return <blockquote key={i}>{inline(lines.map((l) => l.replace(/^>\s?/, '')).join(' '))}</blockquote>;
    }
    return (
      <p key={i}>
        {lines.map((l, j) => (
          <React.Fragment key={j}>
            {j > 0 && <br />}
            {inline(l)}
          </React.Fragment>
        ))}
      </p>
    );
  });
}

function inline(s: string): React.ReactNode {
  const parts = s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('`') && p.endsWith('`')) return <code key={i}>{p.slice(1, -1)}</code>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}
